import React, {useRef, useState} from "react";
import {
    AccountDrilldownReport,
    AccountingAccountList,
    AccountingAccountRestControllerApiFactory,
    AccountingAuditExport,
    AccountingReportRestControllerApiFactory,
    BalanceSheetReport,
    GeneralLedgerReport,
    JournalBookReport,
    LedgerReport,
    OfficialBooks,
    ProfitLossReport,
    TrialBalanceReport,
    WithholdingReceivedLine,
    WithholdingsReceivedReport,
} from "arteco-api-client-ts";
import {
    exportBalanceSheetXls,
    exportGeneralLedgerXls,
    exportJournalXls,
    exportLedgerXls,
    exportProfitLossXls,
    exportTrialBalanceXls,
} from "../../../../components/utils/xlsExport";
import {
    Alert,
    Autocomplete,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Link,
    Menu,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableFooter,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material";
import {TabContext, TabPanel} from "@mui/lab";
import DownloadRoundedIcon from "@mui/icons-material/DownloadRounded";
import KeyboardArrowDownRoundedIcon from "@mui/icons-material/KeyboardArrowDownRounded";
import AssessmentRoundedIcon from "@mui/icons-material/AssessmentRounded";
import {
    EmptyState,
    FilterChips,
    Page,
    PageHeader,
    Panel,
    SkeletonRows,
    SoftChip,
    tableContainerSx,
    tableSx,
} from "../../../../components/kit";
import {useApiManager} from "../../../../components/utils/api";
import {fileLocalSave} from "../../../../components/form/form_inputs/form_file";
import {fmtAmt, fmtAccountCode} from "../../../../components/utils/numbers";
import {dateToString} from "../../../../components/utils/dates";

// Prefijos de cuentas del PGCE para cada línea de la Cuenta de Pérdidas y Ganancias
const PL_PREFIXES: Record<string, string[]> = {
    // Los mismos que AccountingReportService (#14736). El desglose filtra por prefijo, sin exclusiones: en
    // «Otros gastos» salen también 630, 633 y 638, que van al impuesto.
    cifraNegocios: ["70"],
    variacionExistenciasProductos: ["71", "6930", "7930"],
    aprovisionamientos: ["60", "61", "6931", "6932", "6933", "7931", "7932", "7933"],
    otrosIngresosExplotacion: ["740", "747", "75"],
    gastosPersonal: ["64"],
    otrosGastosExplotacion: ["62", "631", "634", "636", "639", "65", "694", "695", "794", "7954"],
    amortizacion: ["68"],
    imputacionSubvenciones: ["746"],
    excesosProvisiones: ["7951", "7952", "7955", "7956"],
    deterioroYEnajenaciones: ["690", "691", "692", "790", "791", "792"],
    otrosResultados: ["670", "671", "672", "678", "770", "771", "772", "778"],
    ingresosFinancieros: ["760", "761", "762", "764", "765", "769"],
    gastosFinancieros: ["660", "661", "662", "664", "665", "669"],
    variacionValorRazonable: ["663", "763"],
    diferenciasCambio: ["768", "668"],
    deterioroInstrFinancieros: ["666", "667", "673", "674", "675", "696", "697", "698", "699", "766", "767", "773", "775", "796", "797", "798", "799"],
    impuestoBeneficios: ["630", "633", "638"],
};

type ReportTab = "trial-balance" | "ledger" | "general-ledger" | "journal" | "profit-loss" | "balance-sheet" | "withholdings-received";

/** Qué fechas pide cada informe (#14733): tramo dentro del año, solo fecha de corte o ninguna. */
const DATE_MODE: Record<ReportTab, "range" | "cutoff" | "none"> = {
    "trial-balance": "range",
    "ledger": "range",
    "general-ledger": "range",
    "journal": "range",
    "profit-loss": "range",
    "balance-sheet": "cutoff",
    "withholdings-received": "none",
};

/** «2026» o «del 01/07/2026 al 30/09/2026». */
const periodText = (year: number, from?: string, to?: string) => {
    const full = (!from || from.endsWith("-01-01")) && (!to || to.endsWith("-12-31"));
    return full ? `${year}` : `del ${dateToString(from || `${year}-01-01`)} al ${dateToString(to || `${year}-12-31`)}`;
};

// ---- Helpers ----

const PyGRow = ({label, value, indent = false, bold = false, dividerAbove = false, onDrilldown}: {
    label: string; value?: number | null; indent?: boolean; bold?: boolean; dividerAbove?: boolean;
    onDrilldown?: () => void;
}) => (
    <>
        {dividerAbove && <TableRow><TableCell colSpan={2} sx={{p: 0}}><Divider/></TableCell></TableRow>}
        <TableRow>
            <TableCell sx={{pl: indent ? 4 : 2, fontWeight: bold ? 700 : 400, fontSize: bold ? "0.9rem" : "0.85rem",
                bgcolor: bold ? "grey.50" : undefined}}>
                {label}
            </TableCell>
            <TableCell align="right" sx={{fontWeight: bold ? 700 : 400, fontSize: bold ? "0.9rem" : "0.85rem", whiteSpace: "nowrap",
                fontVariantNumeric: "tabular-nums", bgcolor: bold ? "grey.50" : undefined}}>
                {value != null
                    ? onDrilldown
                        ? <Link component="button" onClick={onDrilldown} underline="hover" color="inherit" sx={{cursor: "pointer", font: "inherit", fontWeight: "inherit"}}>{fmtAmt(value)}</Link>
                        : fmtAmt(value)
                    : "—"}
            </TableCell>
        </TableRow>
    </>
);

const BSSectionTable = ({section, onDrilldown}: {section: any; onDrilldown?: (prefixes: string[]) => void}) => {
    if (!section) return null;
    return (
        <Box mb={2.5}>
            <Typography variant="subtitle2" sx={{fontWeight: 700, mb: 1}}>
                {section.code}) {section.label}
            </Typography>
            <TableContainer sx={tableContainerSx}>
            <Table size="small" sx={tableSx}>
                <TableBody>
                    {(section.lines ?? []).map((line: any, idx: number) => (
                        <TableRow key={idx}>
                            <TableCell sx={{fontFamily: "monospace", width: 60, fontSize: "0.75rem", color: "text.secondary"}}>{line.code}</TableCell>
                            <TableCell sx={{fontSize: "0.8rem"}}>{line.label}</TableCell>
                            <TableCell align="right" sx={{fontSize: "0.8rem", whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums"}}>
                                {onDrilldown && line.prefixes?.length > 0
                                    ? <Link component="button" onClick={() => onDrilldown(line.prefixes)} underline="hover" color="inherit" sx={{cursor: "pointer", font: "inherit"}}>{fmtAmt(line.amount)}</Link>
                                    : fmtAmt(line.amount)}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
                <TableFooter sx={{"& td": {fontWeight: 700, color: "text.primary", fontSize: 13, bgcolor: "grey.50"}}}>
                    <TableRow>
                        <TableCell colSpan={2}>Total {section.label}</TableCell>
                        <TableCell align="right" sx={{whiteSpace: "nowrap", fontVariantNumeric: "tabular-nums"}}>{fmtAmt(section.total)}</TableCell>
                    </TableRow>
                </TableFooter>
            </Table>
            </TableContainer>
        </Box>
    );
};

// ---- Page ----

export const AccountingReportsPage = () => {
    const apiManager = useApiManager();
    const [tab, setTab] = useState<ReportTab>("trial-balance");
    const [year, setYear] = useState(new Date().getFullYear());
    const [accountCode, setAccountCode] = useState("");
    const [accountOption, setAccountOption] = useState<AccountingAccountList | null>(null);
    const [accountInput, setAccountInput] = useState("");
    const [accountOptions, setAccountOptions] = useState<AccountingAccountList[]>([]);
    const accountTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const [loading, setLoading] = useState(false);
    // Fechas dentro del año elegido (#14733); vacías, el año entero.
    const [dateFrom, setDateFrom] = useState("");
    const [dateTo, setDateTo] = useState("");

    const handleAccountSearch = (term: string) => {
        if (accountTimerRef.current) clearTimeout(accountTimerRef.current);
        if (!term.trim()) { setAccountOptions([]); return; }
        accountTimerRef.current = setTimeout(() => {
            const acctCtrl = apiManager.factory(AccountingAccountRestControllerApiFactory);
            apiManager.execute(
                acctCtrl.listAccountingAccount(undefined, undefined, undefined, undefined, undefined, undefined, term.trim(), false)
            ).then((list) => setAccountOptions(list as AccountingAccountList[]));
        }, 250);
    };

    const [trialBalance, setTrialBalance] = useState<TrialBalanceReport | null>(null);
    const [ledger, setLedger] = useState<LedgerReport | null>(null);
    const [generalLedger, setGeneralLedger] = useState<GeneralLedgerReport | null>(null);
    const [journal, setJournal] = useState<JournalBookReport | null>(null);
    const [profitLoss, setProfitLoss] = useState<ProfitLossReport | null>(null);
    const [balanceSheet, setBalanceSheet] = useState<BalanceSheetReport | null>(null);
    const [withholdingsReceived, setWithholdingsReceived] = useState<WithholdingsReceivedReport | null>(null);
    const [drilldownOpen, setDrilldownOpen] = useState(false);
    const [drilldownData, setDrilldownData] = useState<AccountDrilldownReport | null>(null);
    const [drilldownLoading, setDrilldownLoading] = useState(false);
    const [actionsAnchor, setActionsAnchor] = useState<null | HTMLElement>(null);

    const ctrl = apiManager.factory(AccountingReportRestControllerApiFactory);

    const openDrilldown = (prefixes: string[]) => {
        if (!prefixes?.length) return;
        setDrilldownOpen(true);
        setDrilldownData(null);
        setDrilldownLoading(true);
        apiManager.execute(ctrl.drilldown(year, prefixes.join(",")))
            .then(data => setDrilldownData(data))
            .finally(() => setDrilldownLoading(false));
    };

    const from = dateFrom || undefined;
    const to = dateTo || undefined;
    const fullFrom = dateFrom || `${year}-01-01`;
    const fullTo = dateTo || `${year}-12-31`;

    const loadReport = () => {
        setLoading(true);
        if (tab === "trial-balance") {
            apiManager.execute(ctrl.trialBalance(year, from, to)).then(setTrialBalance).finally(() => setLoading(false));
        } else if (tab === "ledger") {
            if (!accountCode) { setLoading(false); return; }
            apiManager.execute(from || to ? ctrl.statement(accountCode, fullFrom, fullTo) : ctrl.ledger(year, accountCode))
                .then(setLedger).finally(() => setLoading(false));
        } else if (tab === "general-ledger") {
            apiManager.execute(ctrl.generalLedger(fullFrom, fullTo)).then(setGeneralLedger).finally(() => setLoading(false));
        } else if (tab === "journal") {
            apiManager.execute(ctrl.journalBook(fullFrom, fullTo)).then(setJournal).finally(() => setLoading(false));
        } else if (tab === "profit-loss") {
            apiManager.execute(ctrl.profitLoss(year, from, to)).then(setProfitLoss).finally(() => setLoading(false));
        } else if (tab === "balance-sheet") {
            apiManager.execute(ctrl.balanceSheet(year, to)).then(setBalanceSheet).finally(() => setLoading(false));
        } else if (tab === "withholdings-received") {
            apiManager.execute(ctrl.withholdingsReceived(year)).then(setWithholdingsReceived).finally(() => setLoading(false));
        }
    };

    const handleGenerateClosing = () => {
        apiManager.execute(ctrl.generateClosingEntry(year), `Asiento de cierre ${year} generado`);
    };

    const handleGenerateOpening = () => {
        apiManager.execute(ctrl.generateOpeningEntry(year), `Asiento de apertura ${year + 1} generado`);
    };

    const handleAuditExport = () => {
        apiManager.execute(ctrl.auditExport(year)).then((data: AccountingAuditExport) => {
            const blob = new Blob([JSON.stringify(data, null, 2)], {type: "application/json"});
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `auditoria-contable-${year}.json`;
            a.click();
            URL.revokeObjectURL(url);
        });
    };

    const handleOfficialBooks = () => {
        apiManager.execute(ctrl.getOfficialBooks(year)).then((data: OfficialBooks) => {
            const blob = new Blob([JSON.stringify(data, null, 2)], {type: "application/json"});
            const url = URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = `libros-oficiales-${year}.json`;
            a.click();
            URL.revokeObjectURL(url);
        });
    };

    // Los dos libros que se legalizan con Legalia 2 (#14713): DIARIO_001.pdf e INV_CUEN_001.pdf.
    const handleJournalPdf = () => {
        apiManager.executeRaw(ctrl.officialJournalPdf(year)).then((resp) => fileLocalSave(resp));
    };

    const handleInventoryPdf = () => {
        apiManager.executeRaw(ctrl.officialInventoryPdf(year)).then((resp) => fileLocalSave(resp));
    };

    const handleWithholdingsCsv = () => {
        if (!withholdingsReceived || !withholdingsReceived.lines) return;
        const header = "NIF Proveedor;Nombre Proveedor;Fecha Factura;Base Imponible;Retención;Tipo";
        const rows = withholdingsReceived.lines.map((line: WithholdingReceivedLine) =>
            [
                line.supplierNif ?? "",
                line.supplierName ?? "",
                line.issueDate ?? "",
                (line.baseAmount ?? 0).toFixed(2),
                (line.withheldAmount ?? 0).toFixed(2),
                line.retentionType ?? "",
            ].join(";")
        );
        const csv = [header, ...rows].join("\n");
        const blob = new Blob([csv], {type: "text/csv;charset=utf-8;"});
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `retenciones-soportadas-${year}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    const pl = profitLoss as any;
    const bs = balanceSheet as any;
    const num = {fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap"} as const;
    const footSx = {"& td": {fontWeight: 700, color: "text.primary", fontSize: 14, bgcolor: "grey.50"}};

    const reportOptions = [
        {value: "trial-balance" as ReportTab, label: "Sumas y saldos"},
        {value: "ledger" as ReportTab, label: "Libro mayor"},
        {value: "general-ledger" as ReportTab, label: "Mayor de todas las cuentas"},
        {value: "journal" as ReportTab, label: "Libro diario"},
        {value: "profit-loss" as ReportTab, label: "Pérdidas y ganancias"},
        {value: "balance-sheet" as ReportTab, label: "Balance de situación"},
        {value: "withholdings-received" as ReportTab, label: "Retenciones soportadas"},
    ];

    const download = (onClick: () => void, label = "Descargar XLS") => (
        <Button size="small" variant="outlined" startIcon={<DownloadRoundedIcon/>} onClick={onClick}>{label}</Button>
    );

    const empty = (text: string) => (
        <Panel>
            {loading ? <SkeletonRows rows={6} height={36}/> :
                <EmptyState icon={<AssessmentRoundedIcon/>} title={text}/>}
        </Panel>
    );

    return (
        <Page maxWidth={1400}>
            <PageHeader
                title="Informes contables"
                subtitle="Para legalizar, presenta los libros firmados digitalmente en el portal del Registro Mercantil (legalizacion.registradores.org)."
                actions={<>
                    <Button variant="outlined" endIcon={<KeyboardArrowDownRoundedIcon/>}
                            onClick={(e) => setActionsAnchor(e.currentTarget)}>
                        Acciones del ejercicio {year}
                    </Button>
                    <Menu anchorEl={actionsAnchor} open={Boolean(actionsAnchor)} onClose={() => setActionsAnchor(null)}>
                        <MenuItem onClick={() => { setActionsAnchor(null); handleGenerateClosing(); }}>Generar asiento de cierre {year}</MenuItem>
                        <MenuItem onClick={() => { setActionsAnchor(null); handleGenerateOpening(); }}>Generar asiento de apertura {year + 1}</MenuItem>
                        <Divider/>
                        <MenuItem disabled={loading} onClick={() => { setActionsAnchor(null); handleAuditExport(); }}>Exportar auditoría IA {year}</MenuItem>
                        <MenuItem disabled={loading} onClick={() => { setActionsAnchor(null); handleOfficialBooks(); }}>Libros oficiales RM {year} (JSON)</MenuItem>
                        <MenuItem disabled={loading} onClick={() => { setActionsAnchor(null); handleJournalPdf(); }}>Libro Diario (PDF) {year}</MenuItem>
                        <MenuItem disabled={loading} onClick={() => { setActionsAnchor(null); handleInventoryPdf(); }}>Inventarios y Cuentas Anuales (PDF) {year}</MenuItem>
                    </Menu>
                </>}
            />

            <Stack spacing={3}>
                <Panel>
                    <Stack spacing={2}>
                        <FilterChips value={tab} onChange={setTab} options={reportOptions}/>
                        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                            <TextField
                                label="Año"
                                type="number"
                                value={year}
                                onChange={(e) => { setYear(parseInt(e.target.value)); setDateFrom(""); setDateTo(""); }}
                                size="small"
                                sx={{width: 110}}
                            />
                            {DATE_MODE[tab] === "range" && (
                                <TextField label="Desde" type="date" size="small" value={dateFrom}
                                           onChange={(e) => setDateFrom(e.target.value)}
                                           InputLabelProps={{shrink: true}}
                                           inputProps={{min: `${year}-01-01`, max: `${year}-12-31`}}
                                           helperText={dateFrom ? " " : "Vacío: 1 de enero"}
                                           sx={{width: {xs: "calc(50% - 8px)", sm: 165}}}/>
                            )}
                            {DATE_MODE[tab] !== "none" && (
                                <TextField label={DATE_MODE[tab] === "cutoff" ? "Fecha de corte" : "Hasta"} type="date" size="small"
                                           value={dateTo} onChange={(e) => setDateTo(e.target.value)}
                                           InputLabelProps={{shrink: true}}
                                           inputProps={{min: `${year}-01-01`, max: `${year}-12-31`}}
                                           helperText={dateTo ? " " : "Vacío: 31 de diciembre"}
                                           sx={{width: {xs: "calc(50% - 8px)", sm: 165}}}/>
                            )}
                            {tab === "ledger" && (
                                <Autocomplete
                                    size="small"
                                    options={accountOptions}
                                    value={accountOption}
                                    inputValue={accountInput}
                                    filterOptions={(x) => x}
                                    noOptionsText={accountInput.trim() ? "Sin resultados" : "Código o nombre"}
                                    onInputChange={(_, val) => { setAccountInput(val); handleAccountSearch(val); }}
                                    onChange={(_, val) => {
                                        setAccountOption(val);
                                        setAccountCode(val?.code ?? "");
                                    }}
                                    getOptionLabel={(o) => `${fmtAccountCode(o.code)} — ${(o as any).name ?? ""}`}
                                    isOptionEqualToValue={(a, b) => a.id === b.id}
                                    renderOption={(props, option) => (
                                        <li {...props} key={option.id}>
                                            <Box component="span" sx={{fontFamily: "monospace", mr: 1, fontSize: "0.85em", color: "text.secondary"}}>
                                                {fmtAccountCode(option.code)}
                                            </Box>
                                            {(option as any).name}
                                        </li>
                                    )}
                                    renderInput={(params) => (
                                        <TextField {...params} label="Cuenta" placeholder="Código o nombre…" size="small"/>
                                    )}
                                    sx={{width: {xs: "100%", sm: 300}}}
                                />
                            )}
                            <Button variant="contained" onClick={loadReport} disabled={loading}>
                                {loading ? "Cargando…" : "Generar informe"}
                            </Button>
                            {tab === "withholdings-received" && withholdingsReceived && download(handleWithholdingsCsv, "Descargar CSV")}
                        </Box>
                    </Stack>
                </Panel>

                <TabContext value={tab}>
                    {/* ---- Balance de Sumas y Saldos ---- */}
                    <TabPanel value="trial-balance" sx={{p: 0}}>
                        {trialBalance ? (() => {
                            const diff = ((trialBalance as any).totalDebitBalance ?? 0) - ((trialBalance as any).totalCreditBalance ?? 0);
                            const balanced = Math.abs(diff) < 0.01;
                            return (
                                <Panel title={`Balance de sumas y saldos ${periodText(year, trialBalance.dateFrom, trialBalance.dateTo)}`}
                                       subtitle={<Box component="span" sx={{display: "inline-flex", mt: 0.5}}>
                                           {balanced
                                               ? <SoftChip tone="success" label="Saldo = 0: balance cuadrado"/>
                                               : <SoftChip tone="error" label={`Descuadre de ${fmtAmt(diff)}: revisar asientos`}/>}
                                       </Box>}
                                       action={download(() => exportTrialBalanceXls(trialBalance, year))}>
                                    <TableContainer sx={{...tableContainerSx, overflowX: "auto"}}>
                                    <Table size="small" sx={{...tableSx, tableLayout: "fixed", minWidth: 860}}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{width: 90}}>Código</TableCell>
                                                <TableCell>Cuenta</TableCell>
                                                <TableCell align="right" sx={{width: 120}}>Debe</TableCell>
                                                <TableCell align="right" sx={{width: 120}}>Haber</TableCell>
                                                <TableCell align="right" sx={{width: 120}}>Saldo deudor</TableCell>
                                                <TableCell align="right" sx={{width: 120}}>Saldo acreedor</TableCell>
                                                <TableCell align="right" sx={{width: 120}}>Saldo</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(trialBalance.lines ?? []).map((line: any, idx: number) => {
                                                const saldo = (line.debitBalance ?? 0) - (line.creditBalance ?? 0);
                                                return (
                                                    <TableRow key={idx} hover>
                                                        <TableCell sx={{fontFamily: "monospace"}}>{line.accountCode}</TableCell>
                                                        <TableCell sx={{whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}} title={line.accountName}>{line.accountName}</TableCell>
                                                        <TableCell align="right" sx={num}>{fmtAmt(line.totalDebit)}</TableCell>
                                                        <TableCell align="right" sx={num}>{fmtAmt(line.totalCredit)}</TableCell>
                                                        <TableCell align="right" sx={num}>{line.debitBalance > 0 ? fmtAmt(line.debitBalance) : ""}</TableCell>
                                                        <TableCell align="right" sx={num}>{line.creditBalance > 0 ? fmtAmt(line.creditBalance) : ""}</TableCell>
                                                        <TableCell align="right" sx={{...num, fontWeight: 600}}>{fmtAmt(saldo)}</TableCell>
                                                    </TableRow>
                                                );
                                            })}
                                        </TableBody>
                                        <TableFooter sx={footSx}>
                                            <TableRow>
                                                <TableCell colSpan={2}>Totales</TableCell>
                                                <TableCell align="right" sx={num}>{fmtAmt(trialBalance.totalDebit)}</TableCell>
                                                <TableCell align="right" sx={num}>{fmtAmt(trialBalance.totalCredit)}</TableCell>
                                                <TableCell align="right" sx={num}>{fmtAmt(trialBalance.totalDebitBalance)}</TableCell>
                                                <TableCell align="right" sx={num}>{fmtAmt(trialBalance.totalCreditBalance)}</TableCell>
                                                <TableCell align="right" sx={{...num, color: balanced ? undefined : "error.main !important"}}>{fmtAmt(balanced ? 0 : diff)}</TableCell>
                                            </TableRow>
                                        </TableFooter>
                                    </Table>
                                    </TableContainer>
                                </Panel>
                            );
                        })() : empty("Elige el año y pulsa «Generar informe»")}
                    </TabPanel>

                    {/* ---- Libro Mayor ---- */}
                    <TabPanel value="ledger" sx={{p: 0}}>
                        {ledger ? (
                            <Panel title={<><Box component="span" sx={{fontFamily: "monospace", mr: 1}}>{ledger.accountCode}</Box>{ledger.accountName}</>}
                                   subtitle={`Libro mayor ${periodText(year, ledger.dateFrom, ledger.dateTo)}. Nº de asiento del Libro Diario.`}
                                   action={download(() => exportLedgerXls(ledger, year))}>
                                <TableContainer sx={{...tableContainerSx, overflowX: "auto"}}>
                                <Table size="small" sx={{...tableSx, tableLayout: "fixed", minWidth: 720}}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{width: 100}}>Fecha</TableCell>
                                            <TableCell sx={{width: 90}}>Asiento</TableCell>
                                            <TableCell>Descripción</TableCell>
                                            <TableCell align="right" sx={{width: 115}}>Debe</TableCell>
                                            <TableCell align="right" sx={{width: 115}}>Haber</TableCell>
                                            <TableCell align="right" sx={{width: 120}}>Saldo</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        <TableRow>
                                            <TableCell colSpan={5} sx={{color: "text.secondary"}}>Saldo anterior</TableCell>
                                            <TableCell align="right" sx={{...num, fontWeight: 700}}>{fmtAmt(ledger.openingBalance ?? 0)}</TableCell>
                                        </TableRow>
                                        {(ledger.entries ?? []).map((entry: any, idx: number) => (
                                            <TableRow key={idx} hover>
                                                <TableCell sx={num}>{dateToString(entry.entryDate)}</TableCell>
                                                <TableCell sx={{fontFamily: "monospace"}}>{entry.entryNumber}</TableCell>
                                                <TableCell>
                                                    {entry.accountCode && entry.accountCode !== ledger.accountCode && (
                                                        <Box component="span" sx={{fontFamily: "monospace", mr: 1, color: "text.secondary"}}>{entry.accountCode}</Box>
                                                    )}
                                                    {entry.description}
                                                </TableCell>
                                                <TableCell align="right" sx={num}>{fmtAmt(entry.debit != null && entry.debit > 0 ? entry.debit : null)}</TableCell>
                                                <TableCell align="right" sx={num}>{fmtAmt(entry.credit != null && entry.credit > 0 ? entry.credit : null)}</TableCell>
                                                <TableCell align="right" sx={{...num, fontWeight: 700}}>{fmtAmt(entry.runningBalance)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                    <TableFooter sx={footSx}>
                                        <TableRow>
                                            <TableCell colSpan={3}>Totales y saldo final</TableCell>
                                            <TableCell align="right" sx={num}>{fmtAmt(ledger.totalDebit)}</TableCell>
                                            <TableCell align="right" sx={num}>{fmtAmt(ledger.totalCredit)}</TableCell>
                                            <TableCell align="right" sx={num}>{fmtAmt(ledger.finalBalance)}</TableCell>
                                        </TableRow>
                                    </TableFooter>
                                </Table>
                                </TableContainer>
                            </Panel>
                        ) : empty("Elige una cuenta y pulsa «Generar informe»")}
                    </TabPanel>

                    {/* ---- Mayor de todas las cuentas (#14733): resumen en pantalla, detalle en el Excel ---- */}
                    <TabPanel value="general-ledger" sx={{p: 0}}>
                        {generalLedger ? (
                            <Panel title={`Mayor de todas las cuentas ${periodText(year, generalLedger.dateFrom, generalLedger.dateTo)}`}
                                   subtitle={`${(generalLedger.accounts ?? []).length} cuentas con movimiento. El Excel lleva cada cuenta con su saldo inicial, sus movimientos y su saldo final.`}
                                   action={download(() => exportGeneralLedgerXls(generalLedger))}>
                                <TableContainer sx={{...tableContainerSx, overflowX: "auto"}}>
                                <Table size="small" sx={{...tableSx, tableLayout: "fixed", minWidth: 820}}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{width: 100}}>Cuenta</TableCell>
                                            <TableCell>Título</TableCell>
                                            <TableCell align="right" sx={{width: 120}}>Saldo inicial</TableCell>
                                            <TableCell align="right" sx={{width: 120}}>Debe</TableCell>
                                            <TableCell align="right" sx={{width: 120}}>Haber</TableCell>
                                            <TableCell align="right" sx={{width: 120}}>Saldo final</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(generalLedger.accounts ?? []).map((a) => (
                                            <TableRow key={a.accountCode} hover>
                                                <TableCell sx={{fontFamily: "monospace"}}>{a.accountCode}</TableCell>
                                                <TableCell sx={{whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}} title={a.accountName}>{a.accountName}</TableCell>
                                                <TableCell align="right" sx={num}>{fmtAmt(a.openingBalance)}</TableCell>
                                                <TableCell align="right" sx={num}>{fmtAmt(a.totalDebit)}</TableCell>
                                                <TableCell align="right" sx={num}>{fmtAmt(a.totalCredit)}</TableCell>
                                                <TableCell align="right" sx={{...num, fontWeight: 600}}>{fmtAmt(a.finalBalance)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                    <TableFooter sx={footSx}>
                                        <TableRow>
                                            <TableCell colSpan={3}>Total del periodo</TableCell>
                                            <TableCell align="right" sx={num}>{fmtAmt(generalLedger.totalDebit)}</TableCell>
                                            <TableCell align="right" sx={num}>{fmtAmt(generalLedger.totalCredit)}</TableCell>
                                            <TableCell/>
                                        </TableRow>
                                    </TableFooter>
                                </Table>
                                </TableContainer>
                            </Panel>
                        ) : empty("Elige el periodo y pulsa «Generar informe»")}
                    </TabPanel>

                    {/* ---- Libro diario por periodo (#14733): con la numeración del PDF que se legaliza ---- */}
                    <TabPanel value="journal" sx={{p: 0}}>
                        {journal ? (
                            <Panel title={`Libro diario ${periodText(year, journal.dateFrom, journal.dateTo)}`}
                                   subtitle="Numeración del Libro Diario del ejercicio: la misma que el PDF que se legaliza y que el mayor."
                                   action={download(() => exportJournalXls(journal))}>
                                <TableContainer sx={{...tableContainerSx, overflowX: "auto"}}>
                                <Table size="small" sx={{...tableSx, tableLayout: "fixed", minWidth: 720}}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell sx={{width: 90}}>Asiento</TableCell>
                                            <TableCell sx={{width: 100}}>Fecha</TableCell>
                                            <TableCell>Descripción</TableCell>
                                            <TableCell align="right" sx={{width: 120}}>Debe</TableCell>
                                            <TableCell align="right" sx={{width: 120}}>Haber</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {(journal.entries ?? []).map((e) => (
                                            <TableRow key={e.journalEntryId} hover>
                                                <TableCell sx={{fontFamily: "monospace"}}>{e.number}</TableCell>
                                                <TableCell sx={num}>{dateToString(e.date)}</TableCell>
                                                <TableCell sx={{whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis"}} title={e.description}>{e.description}</TableCell>
                                                <TableCell align="right" sx={num}>{fmtAmt(e.totalDebit)}</TableCell>
                                                <TableCell align="right" sx={num}>{fmtAmt(e.totalCredit)}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                    <TableFooter sx={footSx}>
                                        <TableRow>
                                            <TableCell colSpan={3}>Total ({(journal.entries ?? []).length} asientos)</TableCell>
                                            <TableCell align="right" sx={num}>{fmtAmt(journal.totalDebit)}</TableCell>
                                            <TableCell align="right" sx={num}>{fmtAmt(journal.totalCredit)}</TableCell>
                                        </TableRow>
                                    </TableFooter>
                                </Table>
                                </TableContainer>
                            </Panel>
                        ) : empty("Elige el periodo y pulsa «Generar informe»")}
                    </TabPanel>

                    {/* ---- Cuenta de Pérdidas y Ganancias (PGCE modelo abreviado) ---- */}
                    <TabPanel value="profit-loss" sx={{p: 0}}>
                        {pl ? (
                            <Panel title={`Cuenta de pérdidas y ganancias ${periodText(pl.periodYear, pl.dateFrom, pl.dateTo)}`}
                                   subtitle="Modelo abreviado del PGC. Pulsa un importe para ver sus movimientos."
                                   action={download(() => exportProfitLossXls(pl))}>
                                <Box sx={{maxWidth: 760}}>
                                <TableContainer sx={tableContainerSx}>
                                <Table size="small" sx={tableSx}>
                                    <TableBody>
                                        <PyGRow label="1. Importe neto de la cifra de negocios" value={pl.cifraNegocios} indent onDrilldown={() => openDrilldown(PL_PREFIXES.cifraNegocios)}/>
                                        <PyGRow label="2. Variación de existencias de productos terminados y en curso" value={pl.variacionExistenciasProductos} indent onDrilldown={() => openDrilldown(PL_PREFIXES.variacionExistenciasProductos)}/>
                                        <PyGRow label="3. Aprovisionamientos" value={pl.aprovisionamientos} indent onDrilldown={() => openDrilldown(PL_PREFIXES.aprovisionamientos)}/>
                                        <PyGRow label="4. Otros ingresos de explotación" value={pl.otrosIngresosExplotacion} indent onDrilldown={() => openDrilldown(PL_PREFIXES.otrosIngresosExplotacion)}/>
                                        <PyGRow label="5. Gastos de personal" value={pl.gastosPersonal} indent onDrilldown={() => openDrilldown(PL_PREFIXES.gastosPersonal)}/>
                                        <PyGRow label="6. Otros gastos de explotación" value={pl.otrosGastosExplotacion} indent onDrilldown={() => openDrilldown(PL_PREFIXES.otrosGastosExplotacion)}/>
                                        <PyGRow label="7. Amortización del inmovilizado" value={pl.amortizacion} indent onDrilldown={() => openDrilldown(PL_PREFIXES.amortizacion)}/>
                                        <PyGRow label="8. Imputación de subvenciones de inmovilizado no financiero y otras" value={pl.imputacionSubvenciones} indent onDrilldown={() => openDrilldown(PL_PREFIXES.imputacionSubvenciones)}/>
                                        <PyGRow label="9. Excesos de provisiones" value={pl.excesosProvisiones} indent onDrilldown={() => openDrilldown(PL_PREFIXES.excesosProvisiones)}/>
                                        <PyGRow label="10. Deterioro y resultado por enajenaciones del inmovilizado" value={pl.deterioroYEnajenaciones} indent onDrilldown={() => openDrilldown(PL_PREFIXES.deterioroYEnajenaciones)}/>
                                        <PyGRow label="11. Otros resultados" value={pl.otrosResultados} indent onDrilldown={() => openDrilldown(PL_PREFIXES.otrosResultados)}/>
                                        {Number(pl.sinClasificar ?? 0) !== 0 && (
                                            <PyGRow label={`Sin partida del modelo (${(pl.cuentasSinClasificar ?? []).join(", ")})`} value={pl.sinClasificar} indent onDrilldown={() => openDrilldown(pl.cuentasSinClasificar ?? [])}/>
                                        )}
                                        <PyGRow label="A1) Resultado de explotación" value={pl.resultadoExplotacion} bold dividerAbove/>

                                        <PyGRow label="12. Ingresos financieros" value={pl.ingresosFinancieros} indent onDrilldown={() => openDrilldown(PL_PREFIXES.ingresosFinancieros)}/>
                                        <PyGRow label="13. Gastos financieros" value={pl.gastosFinancieros} indent onDrilldown={() => openDrilldown(PL_PREFIXES.gastosFinancieros)}/>
                                        <PyGRow label="14. Variación de valor razonable en instrumentos financieros" value={pl.variacionValorRazonable} indent onDrilldown={() => openDrilldown(PL_PREFIXES.variacionValorRazonable)}/>
                                        <PyGRow label="15. Diferencias de cambio" value={pl.diferenciasCambio} indent onDrilldown={() => openDrilldown(PL_PREFIXES.diferenciasCambio)}/>
                                        <PyGRow label="16. Deterioro y resultado por enajenaciones de instrumentos financieros" value={pl.deterioroInstrFinancieros} indent onDrilldown={() => openDrilldown(PL_PREFIXES.deterioroInstrFinancieros)}/>
                                        <PyGRow label="A2) Resultado financiero" value={pl.resultadoFinanciero} bold dividerAbove/>

                                        <PyGRow label="A3) Resultado antes de impuestos" value={pl.resultadoAntesImpuestos} bold dividerAbove/>

                                        <PyGRow label="17. Impuesto sobre beneficios" value={pl.impuestoBeneficios} indent onDrilldown={() => openDrilldown(PL_PREFIXES.impuestoBeneficios)}/>
                                        <PyGRow label="A4) Resultado del ejercicio" value={pl.resultadoEjercicio} bold dividerAbove/>
                                    </TableBody>
                                </Table>
                                </TableContainer>
                                </Box>
                            </Panel>
                        ) : empty("Elige el año y pulsa «Generar informe»")}
                    </TabPanel>

                    {/* ---- Balance de Situación (PGCE modelo abreviado) ---- */}
                    <TabPanel value="balance-sheet" sx={{p: 0}}>
                        {bs ? (
                            <Panel title={`Balance de situación a ${dateToString(bs.dateTo ?? `${bs.periodYear}-12-31`)}`}
                                   subtitle="Modelo abreviado del PGC. Cada cuenta de los grupos 4 y 5 va al activo o al pasivo según el signo de su saldo. Pulsa un importe para ver sus movimientos."
                                   action={download(() => exportBalanceSheetXls(bs))}>
                                {/* Rejilla CSS y no Grid: el margen negativo de Grid desborda dentro de un Stack. */}
                                <Box sx={{display: "grid", gridTemplateColumns: {xs: "minmax(0, 1fr)", md: "minmax(0, 1fr) minmax(0, 1fr)"}, gap: 4}}>
                                    <Box>
                                        <Typography variant="overline" color="text.secondary" display="block" mb={1}>Activo</Typography>
                                        <BSSectionTable section={bs.activoNoCorriente} onDrilldown={openDrilldown}/>
                                        <BSSectionTable section={bs.activoCorriente} onDrilldown={openDrilldown}/>
                                        <Box display="flex" justifyContent="space-between" sx={{mt: 1, p: 1.5, borderRadius: 2, bgcolor: "grey.100"}}>
                                            <Typography fontWeight={700}>Total activo</Typography>
                                            <Typography fontWeight={700} sx={num}>{fmtAmt(bs.totalActivo)}</Typography>
                                        </Box>
                                    </Box>
                                    <Box>
                                        <Typography variant="overline" color="text.secondary" display="block" mb={1}>Patrimonio neto y pasivo</Typography>
                                        <BSSectionTable section={bs.patrimonioNeto} onDrilldown={openDrilldown}/>
                                        <BSSectionTable section={bs.pasivoNoCorriente} onDrilldown={openDrilldown}/>
                                        <BSSectionTable section={bs.pasivoCorriente} onDrilldown={openDrilldown}/>
                                        <Box display="flex" justifyContent="space-between" sx={{mt: 1, p: 1.5, borderRadius: 2, bgcolor: "grey.100"}}>
                                            <Typography fontWeight={700}>Total patrimonio neto y pasivo</Typography>
                                            <Typography fontWeight={700} sx={num}>{fmtAmt(bs.totalPatrimonioNetoYPasivo)}</Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Panel>
                        ) : empty("Elige el año y pulsa «Generar informe»")}
                    </TabPanel>

                    {/* ---- Retenciones Soportadas ---- */}
                    <TabPanel value="withholdings-received" sx={{p: 0}}>
                        {withholdingsReceived ? (
                            <Stack spacing={3}>
                                <Panel title={`Retenciones soportadas ${year}`}>
                                    <TableContainer sx={{...tableContainerSx, overflowX: "auto"}}>
                                    <Table size="small" sx={{...tableSx, tableLayout: "fixed", minWidth: 760}}>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell sx={{width: 120}}>NIF proveedor</TableCell>
                                                <TableCell>Proveedor</TableCell>
                                                <TableCell sx={{width: 110}}>Fecha factura</TableCell>
                                                <TableCell align="right" sx={{width: 125}}>Base imponible</TableCell>
                                                <TableCell align="right" sx={{width: 115}}>Retención</TableCell>
                                                <TableCell sx={{width: 90}}>Tipo</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {(withholdingsReceived.lines ?? []).map((line: WithholdingReceivedLine, idx: number) => (
                                                <TableRow key={idx} hover>
                                                    <TableCell sx={{fontFamily: "monospace"}}>{line.supplierNif}</TableCell>
                                                    <TableCell>{line.supplierName}</TableCell>
                                                    <TableCell sx={num}>{dateToString(line.issueDate)}</TableCell>
                                                    <TableCell align="right" sx={num}>{fmtAmt(line.baseAmount)}</TableCell>
                                                    <TableCell align="right" sx={num}>{fmtAmt(line.withheldAmount)}</TableCell>
                                                    <TableCell>{line.retentionType}</TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                        <TableFooter sx={footSx}>
                                            <TableRow>
                                                <TableCell colSpan={3}>Totales</TableCell>
                                                <TableCell align="right" sx={num}>{fmtAmt(withholdingsReceived.totalBase)}</TableCell>
                                                <TableCell align="right" sx={num}>{fmtAmt(withholdingsReceived.totalWithheld)}</TableCell>
                                                <TableCell/>
                                            </TableRow>
                                        </TableFooter>
                                    </Table>
                                    </TableContainer>
                                </Panel>
                                {withholdingsReceived.bySupplier && Object.keys(withholdingsReceived.bySupplier).length > 0 && (
                                    <Panel title="Resumen por proveedor">
                                        <TableContainer sx={{...tableContainerSx, maxWidth: 440}}>
                                        <Table size="small" sx={tableSx}>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>NIF</TableCell>
                                                    <TableCell align="right">Total retención</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {Object.entries(withholdingsReceived.bySupplier).map(([nif, total]) => (
                                                    <TableRow key={nif}>
                                                        <TableCell sx={{fontFamily: "monospace"}}>{nif}</TableCell>
                                                        <TableCell align="right" sx={num}>{fmtAmt(total as number)}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                        </TableContainer>
                                    </Panel>
                                )}
                            </Stack>
                        ) : empty("Elige el año y pulsa «Generar informe»")}
                    </TabPanel>
                </TabContext>
            </Stack>

            {/* ---- Dialog de desglose de movimientos ---- */}
            <Dialog open={drilldownOpen} onClose={() => setDrilldownOpen(false)} maxWidth="xl" fullWidth PaperProps={{sx: {borderRadius: 3}}}>
                <DialogTitle sx={{fontWeight: 700}}>
                    Desglose del ejercicio {year}
                    {drilldownData?.prefixes?.length ? (
                        <Typography variant="body2" color="text.secondary">Cuentas: {drilldownData.prefixes.join(", ")}</Typography>
                    ) : null}
                </DialogTitle>
                <DialogContent dividers sx={{p: 0}}>
                    {drilldownLoading && (
                        <Box display="flex" justifyContent="center" p={4}><CircularProgress/></Box>
                    )}
                    {!drilldownLoading && drilldownData && (
                        <TableContainer sx={{overflowX: "auto"}}>
                        <Table size="small" stickyHeader sx={{...tableSx, border: 0, minWidth: 900}}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{width: 95}}>Fecha</TableCell>
                                    <TableCell sx={{width: 75}}>Asiento</TableCell>
                                    <TableCell>Descripción del asiento</TableCell>
                                    <TableCell sx={{width: 220}}>Cuenta</TableCell>
                                    <TableCell>Concepto</TableCell>
                                    <TableCell align="right" sx={{width: 115}}>Debe</TableCell>
                                    <TableCell align="right" sx={{width: 115}}>Haber</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(drilldownData.lines ?? []).map((line, idx) => (
                                    <TableRow key={idx} hover>
                                        <TableCell sx={{...num, fontSize: "0.8rem"}}>{dateToString(line.entryDate)}</TableCell>
                                        <TableCell sx={{fontFamily: "monospace", fontSize: "0.8rem"}}>{line.entryNumber}</TableCell>
                                        <TableCell sx={{fontSize: "0.8rem"}}>{line.entryDescription}</TableCell>
                                        <TableCell sx={{fontSize: "0.8rem"}}>
                                            <Box component="span" sx={{fontFamily: "monospace", mr: 0.75}}>{line.accountCode}</Box>{line.accountName}
                                        </TableCell>
                                        <TableCell sx={{fontSize: "0.8rem", color: "text.secondary"}}>{line.lineDescription}</TableCell>
                                        <TableCell align="right" sx={{...num, fontSize: "0.8rem"}}>{fmtAmt(line.debit)}</TableCell>
                                        <TableCell align="right" sx={{...num, fontSize: "0.8rem"}}>{fmtAmt(line.credit)}</TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                            <TableFooter sx={footSx}>
                                <TableRow>
                                    <TableCell colSpan={5}>Total</TableCell>
                                    <TableCell align="right" sx={num}>{fmtAmt(drilldownData.totalDebit)}</TableCell>
                                    <TableCell align="right" sx={num}>{fmtAmt(drilldownData.totalCredit)}</TableCell>
                                </TableRow>
                                <TableRow>
                                    <TableCell colSpan={5}>Saldo neto (debe − haber)</TableCell>
                                    <TableCell colSpan={2} align="right" sx={num}>
                                        {fmtAmt((drilldownData.totalDebit ?? 0) - (drilldownData.totalCredit ?? 0))}
                                    </TableCell>
                                </TableRow>
                            </TableFooter>
                        </Table>
                        </TableContainer>
                    )}
                    {!drilldownLoading && drilldownData && drilldownData.lines?.length === 0 && (
                        <Box p={2}><Alert severity="info" sx={{borderRadius: 2}}>No hay movimientos para estas cuentas en el ejercicio {year}.</Alert></Box>
                    )}
                </DialogContent>
                <DialogActions sx={{px: 3, py: 1.5}}>
                    <Button onClick={() => setDrilldownOpen(false)}>Cerrar</Button>
                </DialogActions>
            </Dialog>
        </Page>
    );
};
